import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FileCheck,
  FileText,
  LayoutGrid,
  Table,
  ChevronDown,
  ChevronRight,
  List,
  ChevronLeft,
  Users,
  Filter,
} from "lucide-react";
import OperationsDashboardCard from "./OperationsDashboardCard";
import OperationsDashboardModal from "./OperationsDashboardModal";
import ModelQuotesCard from "./ModelQuotesCard";
import ModelQuotesModal from "./ModelQuotesModal";
import QuoteSearchBar from "./QuoteSearchBar";
import ClientsTable from "./ClientsTable";
import ClientDetailsModal from "./ClientDetailsModal";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import { ProjectService } from "../services/projectService";
import { ClientService } from "../services/clientService";
import { Button, SearchBar } from "./ui";
import "./OperationsDashboard.css";

const OperationsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [allModelQuotes, setAllModelQuotes] = useState([]);
  const [clientsWithAnalytics, setClientsWithAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState("quotes-agreements");
  const [currentPage, setCurrentPage] = useState(1);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [quotesSearchQuery, setQuotesSearchQuery] = useState("");
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientStatusFilter, setClientStatusFilter] = useState("Active Client");
  const [clientServicesFilter, setClientServicesFilter] = useState([]);
  const [clientItemsPerPage, setClientItemsPerPage] = useState(20);
  const [clientCurrentPage, setClientCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Collapsed sections state for model quotes
  const [collapsedSections, setCollapsedSections] = useState({
    "to-quote": false,
    quoted: true,
    accepted: true,
    declined: true,
    "action-quotes": false,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        allProjects,
        ,
        allQuotesData,
        clientsData,
        contactsData,
        clientsAnalytics,
      ] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getModelQuotes(),
        ProjectService.getAllModelQuotes(),
        ProjectService.getClients(),
        ProjectService.getContacts(),
        ClientService.getClientsWithAnalytics(),
      ]);

      setProjects(allProjects);
      setAllModelQuotes(allQuotesData);
      setClients(clientsData);
      setContacts(contactsData);
      setClientsWithAnalytics(clientsAnalytics);
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const quotesAndAgreementsProjects = useMemo(() => {
    const filtered = projects
      .filter((p) => {
        // Exclude projects with voided billing status
        if (p.billingStatus === "Voided") {
          return false;
        }

        // Check if billing is NOT complete
        const billingComplete =
          p.billingStatus &&
          ["Quoted", "Invoiced", "Paid"].includes(p.billingStatus);

        // Check if agreement is NOT complete - using exact database enum values
        const agreementComplete =
          p.agreementStatus &&
          [
            "Ready",
            "Utility Ready",
            "Sent",
            "Completed",
            "None needed",
            "Revision-none",
            "Voided",
            "Hold",
            "Not Signed",
          ].includes(p.agreementStatus);

        // Include project if EITHER billing OR agreement is not complete
        const shouldInclude = !billingComplete || !agreementComplete;

        // If it doesn't meet the basic criteria, exclude it
        if (!shouldInclude) {
          return false;
        }

        // Additional filter for revision projects
        // Check if this is a revision project (rev is not 0 and not null)
        const isRevisionProject = p.rev !== 0 && p.rev !== null;
        
        // For revision projects, ALSO require they be ready to export or completed
        if (isRevisionProject) {
          const isReadyOrCompleted = 
            p.modelingStatus === "Ready to Export" || 
            p.modelingStatus === "Completed";
          return isReadyOrCompleted;
        }

        // Non-revision projects that passed the billing/agreement check are included
        return true;
      })
      .sort((a, b) => {
        const aNum = a.jobNumber
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        const bNum = b.jobNumber
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        return bNum - aNum;
      });

    return filtered;
  }, [projects]);

  const handleCardClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleQuoteCardClick = (quote) => {
    setSelectedQuote(quote);
    setQuoteModalOpen(true);
  };

  const handleSearchQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setQuoteModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  const handleQuoteModalClose = () => {
    setQuoteModalOpen(false);
    setSelectedQuote(null);
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setClientModalOpen(true);
  };

  const handleClientModalClose = () => {
    setClientModalOpen(false);
    setSelectedClient(null);
  };

  const toggleSectionCollapse = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleModalSave = useCallback(async () => {
    const currentProjectId = selectedProject?.id;
    // Load fresh data
    try {
      setLoading(true);
      setError(null);

      const [
        allProjects,
        ,
        allQuotesData,
        clientsData,
        contactsData,
      ] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getModelQuotes(),
        ProjectService.getAllModelQuotes(),
        ProjectService.getClients(),
        ProjectService.getContacts(),
      ]);

      setProjects(allProjects);
      setAllModelQuotes(allQuotesData);
      setClients(clientsData);
      setContacts(contactsData);

      // Find and update the selected project with fresh data
      if (currentProjectId) {
        const updatedProject = allProjects.find(
          (p) => p.id === currentProjectId,
        );
        if (updatedProject) {
          setSelectedProject(updatedProject);
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  const handleProjectDelete = useCallback(async (projectId) => {
    // Refresh the projects list after deletion
    try {
      setLoading(true);
      setError(null);

      const [
        allProjects,
        ,
        allQuotesData,
        clientsData,
        contactsData,
      ] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getModelQuotes(),
        ProjectService.getAllModelQuotes(),
        ProjectService.getClients(),
        ProjectService.getContacts(),
      ]);

      setProjects(allProjects);
      setAllModelQuotes(allQuotesData);
      setClients(clientsData);
      setContacts(contactsData);
    } catch (err) {
      console.error("Error loading data after delete:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQuoteModalSave = useCallback(async () => {
    const currentQuoteId = selectedQuote?.id;
    // Load fresh data
    try {
      setLoading(true);
      setError(null);

      const [, allQuotesData, clientsData, contactsData] =
        await Promise.all([
          ProjectService.getModelQuotes(),
          ProjectService.getAllModelQuotes(),
          ProjectService.getClients(),
          ProjectService.getContacts(),
        ]);

      setAllModelQuotes(allQuotesData);
      setClients(clientsData);
      setContacts(contactsData);

      // Find and update the selected quote with fresh data
      if (currentQuoteId) {
        const updatedQuote =
          allQuotesData.find((q) => q.id === currentQuoteId);
        if (updatedQuote) {
          setSelectedQuote(updatedQuote);
        } else {
          // Quote was deleted, clear the selection
          console.log("Quote was deleted, clearing selection");
          setSelectedQuote(null);
        }
      }
    } catch (err) {
      console.error("Error loading quotes:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedQuote]);

  // eslint-disable-next-line no-unused-vars
  const handleClientUpdate = useCallback(async (clientId, updatedData) => {
    // If clientId is provided, just update that specific client without reloading everything
    if (clientId && updatedData) {
      setClientsWithAnalytics(prev =>
        prev.map(client =>
          client.id === clientId
            ? { ...client, ...updatedData }
            : client
        )
      );
      return;
    }

    // Otherwise, do a full reload (for manual refresh)
    try {
      setLoading(true);
      setError(null);

      const clientsAnalytics = await ClientService.getClientsWithAnalytics();
      setClientsWithAnalytics(clientsAnalytics);
    } catch (err) {
      console.error("Error loading clients:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Group quotes by status
  const toQuoteQuotes = useMemo(() => {
    return allModelQuotes
      .filter((quote) => quote.quote_status === "To Quote")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [allModelQuotes]);

  const quotedQuotes = useMemo(() => {
    return allModelQuotes
      .filter((quote) => quote.quote_status === "Quoted")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [allModelQuotes]);

  const acceptedQuotes = useMemo(() => {
    return allModelQuotes
      .filter((quote) => quote.quote_status === "Accepted")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [allModelQuotes]);

  const declinedQuotes = useMemo(() => {
    return allModelQuotes
      .filter((quote) => quote.quote_status === "Declined")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [allModelQuotes]);

  // All projects sorted by job number (newest first)
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aNum = a.jobNumber
        ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
        : 0;
      const bNum = b.jobNumber
        ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
        : 0;
      return bNum - aNum;
    });
  }, [projects]);

  // Filtered projects based on search query
  const filteredProjects = useMemo(() => {
    if (!projectSearchQuery.trim()) return sortedProjects;
    
    const query = projectSearchQuery.toLowerCase();
    return sortedProjects.filter(project => {
      return (
        project.projectName?.toLowerCase().includes(query) ||
        project.jobNumber?.toLowerCase().includes(query) ||
        project.clientName?.toLowerCase().includes(query) ||
        project.contactName?.toLowerCase().includes(query) ||
        project.addressForProject?.toLowerCase().includes(query)
      );
    });
  }, [sortedProjects, projectSearchQuery]);

  // Filtered quotes and agreements projects based on search query
  const filteredQuotesAndAgreements = useMemo(() => {
    if (!quotesSearchQuery.trim()) return quotesAndAgreementsProjects;
    
    const query = quotesSearchQuery.toLowerCase();
    return quotesAndAgreementsProjects.filter(project => {
      return (
        project.projectName?.toLowerCase().includes(query) ||
        project.jobNumber?.toLowerCase().includes(query) ||
        project.clientName?.toLowerCase().includes(query) ||
        project.contactName?.toLowerCase().includes(query) ||
        project.addressForProject?.toLowerCase().includes(query)
      );
    });
  }, [quotesAndAgreementsProjects, quotesSearchQuery]);

  // Paginated projects
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, currentPage]);

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [projectSearchQuery]);

  // Filtered clients based on search and status filter
  const filteredClients = useMemo(() => {
    let filtered = clientsWithAnalytics;

    // Apply status filter (default: Active Client)
    if (clientStatusFilter && clientStatusFilter !== 'All') {
      filtered = filtered.filter(client => client.client_status === clientStatusFilter);
    }

    // Apply services filter (multi-select)
    if (clientServicesFilter.length > 0) {
      filtered = filtered.filter(client => {
        const services = client.services_provided || [];
        // Check if client has ANY of the selected services
        // The database has malformed data with extra braces and quotes, so we need to check for partial matches
        return clientServicesFilter.some(selectedService => {
          return services.some(service => {
            // Check if the service string contains the selected service name
            // This handles variations like {\"3D Models\"}, {"3D Models"}, 3D Models, etc.
            return service.includes(selectedService);
          });
        });
      });
    }

    // Apply search filter
    if (clientSearchQuery.trim()) {
      const query = clientSearchQuery.toLowerCase();
      filtered = filtered.filter(client => {
        return (
          client.name?.toLowerCase().includes(query) ||
          client.state?.toLowerCase().includes(query) ||
          client.client_of?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [clientsWithAnalytics, clientStatusFilter, clientServicesFilter, clientSearchQuery]);

  // Paginated clients
  const paginatedClients = useMemo(() => {
    if (clientItemsPerPage === 'all') {
      return filteredClients;
    }
    const startIndex = (clientCurrentPage - 1) * clientItemsPerPage;
    const endIndex = startIndex + clientItemsPerPage;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, clientCurrentPage, clientItemsPerPage]);

  const clientTotalPages = clientItemsPerPage === 'all'
    ? 1
    : Math.ceil(filteredClients.length / clientItemsPerPage);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setClientCurrentPage(1);
  }, [clientSearchQuery, clientStatusFilter, clientServicesFilter]);

  // Handle services filter toggle
  const toggleServiceFilter = (service) => {
    setClientServicesFilter(prev => {
      if (prev.includes(service)) {
        return prev.filter(s => s !== service);
      } else {
        return [...prev, service];
      }
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        padding: 'var(--space-6)'
      }}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--dark-bg)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-6) var(--space-6) var(--space-4)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '1px solid var(--gray-600)',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-3xl)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--gray-50)',
              }}
            >
              Operations Manager
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              {activeTab === 'quotes-agreements'
                ? `${quotesAndAgreementsProjects.length} action item${quotesAndAgreementsProjects.length !== 1 ? 's' : ''}`
                : activeTab === 'model-quotes'
                ? `${allModelQuotes.length} model quote${allModelQuotes.length !== 1 ? 's' : ''}`
                : activeTab === 'all-projects'
                ? `${projects.length} total project${projects.length !== 1 ? 's' : ''}`
                : `${clientsWithAnalytics.length} client${clientsWithAnalytics.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs and Search */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-6)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '2px solid var(--gray-700)',
          gap: 'var(--space-6)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', paddingBottom: '2px', borderBottom: '2px solid transparent', marginBottom: '-2px' }}>
          <button
            onClick={() => {
              setActiveTab("quotes-agreements");
              setProjectSearchQuery("");
              setQuotesSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'quotes-agreements' ? 'ops-tab-active' : ''}`}
          >
            <LayoutGrid size={16} />
            Action Items ({quotesAndAgreementsProjects.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("model-quotes");
              setProjectSearchQuery("");
              setQuotesSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'model-quotes' ? 'ops-tab-active' : ''}`}
          >
            <Table size={16} />
            3D Model Quotes ({allModelQuotes.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("all-projects");
              setCurrentPage(1);
              setQuotesSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'all-projects' ? 'ops-tab-active' : ''}`}
          >
            <List size={16} />
            All Projects ({projects.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("all-clients");
              setClientSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'all-clients' ? 'ops-tab-active' : ''}`}
          >
            <Users size={16} />
            All Clients ({clientsWithAnalytics.length})
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {activeTab === "quotes-agreements" ? (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-5)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <SearchBar
                  value={quotesSearchQuery}
                  onChange={(e) => setQuotesSearchQuery(e.target.value)}
                  placeholder="Search action items by name, job number, client, contact, or address..."
                  size="sm"
                />
              </div>
              {quotesSearchQuery && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  Found {filteredQuotesAndAgreements.length} projects
                </div>
              )}
            </div>

            {filteredQuotesAndAgreements.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <FileCheck size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)',
                }}>
                  {quotesSearchQuery
                    ? `No projects found matching "${quotesSearchQuery}"`
                    : "No action items requiring attention"
                  }
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: 'var(--space-5)',
                }}
              >
                {filteredQuotesAndAgreements.map((project) => (
                  <OperationsDashboardCard
                    key={project.id}
                    project={project}
                    onClick={() => handleCardClick(project)}
                  />
                ))}
              </div>
            )}

            {/* 3D Model Quote Requests Section - To Quote Status */}
            {toQuoteQuotes.length > 0 && (
              <div style={{ marginTop: 'var(--space-8)' }}>
                <div
                  onClick={() => toggleSectionCollapse("action-quotes")}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-4)',
                    background: 'var(--dark-elevated)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--gray-600)',
                    cursor: 'pointer',
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {collapsedSections["action-quotes"] ? (
                    <ChevronRight size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                  ) : (
                    <ChevronDown size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                  )}
                  <FileText size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                  <h2 style={{
                    margin: 0,
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                  }}>
                    3D Model Quote Requests
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--warning)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {toQuoteQuotes.length}
                  </span>
                </div>
                {!collapsedSections["action-quotes"] && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: 'var(--space-5)',
                    }}
                  >
                    {toQuoteQuotes.map((quote) => (
                      <ModelQuotesCard
                        key={quote.id}
                        quote={quote}
                        clients={clients}
                        contacts={contacts}
                        onClick={() => handleQuoteCardClick(quote)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === "model-quotes" ? (
          <div>
            {/* Custom Search Bar */}
            <div style={{ marginBottom: 'var(--space-5)' }}>
              <QuoteSearchBar
                quotes={allModelQuotes}
                onQuoteSelect={handleSearchQuoteSelect}
              />
            </div>
            {/* To Quote Status Section - Always show */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <div
                onClick={() => toggleSectionCollapse("to-quote")}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  background: 'var(--dark-elevated)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-600)',
                  cursor: 'pointer',
                  marginBottom: 'var(--space-4)',
                }}
              >
                {collapsedSections["to-quote"] ? (
                  <ChevronRight size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                ) : (
                  <ChevronDown size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                )}
                <FileText size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                <h2 style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  To Quote
                </h2>
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--warning)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                }}>
                  {toQuoteQuotes.length}
                </span>
              </div>
              {!collapsedSections["to-quote"] &&
                (toQuoteQuotes.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'var(--space-12)',
                      color: 'var(--gray-400)',
                    }}
                  >
                    <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
                    <p style={{
                      fontSize: 'var(--text-lg)',
                      margin: 0,
                      fontWeight: 'var(--font-medium)',
                    }}>
                      No quotes to process
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: 'var(--space-5)',
                    }}
                  >
                    {toQuoteQuotes.map((quote) => (
                      <ModelQuotesCard
                        key={quote.id}
                        quote={quote}
                        clients={clients}
                        contacts={contacts}
                        onClick={() => handleQuoteCardClick(quote)}
                      />
                    ))}
                  </div>
                ))}
            </div>

            {/* Quoted Status Section - Always show */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <div
                onClick={() => toggleSectionCollapse("quoted")}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  background: 'var(--dark-elevated)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-600)',
                  cursor: 'pointer',
                  marginBottom: 'var(--space-4)',
                }}
              >
                {collapsedSections["quoted"] ? (
                  <ChevronRight size={20} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                ) : (
                  <ChevronDown size={20} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                )}
                <FileText size={20} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                <h2 style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  Quoted
                </h2>
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--cta-blue)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                }}>
                  {quotedQuotes.length}
                </span>
              </div>
              {!collapsedSections["quoted"] &&
                (quotedQuotes.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'var(--space-12)',
                      color: 'var(--gray-400)',
                    }}
                  >
                    <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
                    <p style={{
                      fontSize: 'var(--text-lg)',
                      margin: 0,
                      fontWeight: 'var(--font-medium)',
                    }}>
                      No quoted requests
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: 'var(--space-5)',
                    }}
                  >
                    {quotedQuotes.map((quote) => (
                      <ModelQuotesCard
                        key={quote.id}
                        quote={quote}
                        clients={clients}
                        contacts={contacts}
                        onClick={() => handleQuoteCardClick(quote)}
                      />
                    ))}
                  </div>
                ))}
            </div>

            {/* Accepted Status Section - Always show */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <div
                onClick={() => toggleSectionCollapse("accepted")}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  background: 'var(--dark-elevated)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-600)',
                  cursor: 'pointer',
                  marginBottom: 'var(--space-4)',
                }}
              >
                {collapsedSections["accepted"] ? (
                  <ChevronRight size={20} style={{ color: 'var(--success)', flexShrink: 0 }} />
                ) : (
                  <ChevronDown size={20} style={{ color: 'var(--success)', flexShrink: 0 }} />
                )}
                <FileText size={20} style={{ color: 'var(--success)', flexShrink: 0 }} />
                <h2 style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  Accepted
                </h2>
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--success)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                }}>
                  {acceptedQuotes.length}
                </span>
              </div>
              {!collapsedSections["accepted"] &&
                (acceptedQuotes.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'var(--space-12)',
                      color: 'var(--gray-400)',
                    }}
                  >
                    <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
                    <p style={{
                      fontSize: 'var(--text-lg)',
                      margin: 0,
                      fontWeight: 'var(--font-medium)',
                    }}>
                      No accepted quotes
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: 'var(--space-5)',
                    }}
                  >
                    {acceptedQuotes.map((quote) => (
                      <ModelQuotesCard
                        key={quote.id}
                        quote={quote}
                        clients={clients}
                        contacts={contacts}
                        onClick={() => handleQuoteCardClick(quote)}
                      />
                    ))}
                  </div>
                ))}
            </div>

            {/* Declined Status Section - Always show */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <div
                onClick={() => toggleSectionCollapse("declined")}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  background: 'var(--dark-elevated)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-600)',
                  cursor: 'pointer',
                  marginBottom: 'var(--space-4)',
                }}
              >
                {collapsedSections["declined"] ? (
                  <ChevronRight size={20} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                ) : (
                  <ChevronDown size={20} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                )}
                <FileText size={20} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                <h2 style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  Declined
                </h2>
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--danger)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                }}>
                  {declinedQuotes.length}
                </span>
              </div>
              {!collapsedSections["declined"] &&
                (declinedQuotes.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'var(--space-12)',
                      color: 'var(--gray-400)',
                    }}
                  >
                    <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
                    <p style={{
                      fontSize: 'var(--text-lg)',
                      margin: 0,
                      fontWeight: 'var(--font-medium)',
                    }}>
                      No declined quotes
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: 'var(--space-5)',
                    }}
                  >
                    {declinedQuotes.map((quote) => (
                      <ModelQuotesCard
                        key={quote.id}
                        quote={quote}
                        clients={clients}
                        contacts={contacts}
                        onClick={() => handleQuoteCardClick(quote)}
                      />
                    ))}
                  </div>
                ))}
            </div>
          </div>
        ) : activeTab === "all-projects" ? (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-5)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <SearchBar
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  placeholder="Search projects by name, job number, client, contact, or address..."
                  size="sm"
                />
              </div>
              {projectSearchQuery && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  Found {filteredProjects.length} projects
                </div>
              )}
            </div>

            {filteredProjects.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <List size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)',
                }}>
                  No projects found
                </p>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 'var(--space-5)',
                  }}
                >
                  {paginatedProjects.map((project) => (
                    <OperationsDashboardCard
                      key={project.id}
                      project={project}
                      onClick={() => handleCardClick(project)}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 'var(--space-5)',
                      marginTop: 'var(--space-8)',
                      paddingTop: 'var(--space-5)',
                      borderTop: '1px solid var(--gray-600)',
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>

                    <div style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-400)',
                      fontWeight: 'var(--font-medium)',
                    }}>
                      Page {currentPage} of {totalPages}
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : activeTab === "all-clients" ? (
          <div>
            {/* Client Status Filter and Search */}
            <div style={{ marginBottom: 'var(--space-5)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <Filter size={18} style={{ color: 'var(--gray-400)' }} />
                  <select
                    value={clientStatusFilter}
                    onChange={(e) => setClientStatusFilter(e.target.value)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--gray-600)',
                      background: 'var(--dark-elevated)',
                      color: 'var(--gray-50)',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="Active Client">Active Clients</option>
                    <option value="Part Time Client">Part Time Clients</option>
                    <option value="All">All Clients</option>
                    <option value="Dropped">Dropped</option>
                    <option value="Lost Client">Lost Clients</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>Services:</span>
                  <Button
                    variant={clientServicesFilter.includes('3D Models') ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => toggleServiceFilter('3D Models')}
                  >
                    3D Models
                  </Button>
                  <Button
                    variant={clientServicesFilter.includes('Takeoffs') ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => toggleServiceFilter('Takeoffs')}
                  >
                    Takeoffs
                  </Button>
                  {clientServicesFilter.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setClientServicesFilter([])}
                    >
                      Clear ({clientServicesFilter.length})
                    </Button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>Show:</span>
                  <select
                    value={clientItemsPerPage}
                    onChange={(e) => {
                      const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                      setClientItemsPerPage(value);
                      setClientCurrentPage(1);
                    }}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--gray-600)',
                      background: 'var(--dark-elevated)',
                      color: 'var(--gray-50)',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>
              <SearchBar
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                placeholder="Search clients by name, state, platform, or client of..."
                size="sm"
              />
              {clientSearchQuery && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  marginTop: 'var(--space-2)',
                }}>
                  Found {filteredClients.length} clients
                </div>
              )}
            </div>

            {filteredClients.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <Users size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)',
                }}>
                  {clientSearchQuery || clientStatusFilter !== 'Active Client'
                    ? "No clients found matching filters"
                    : "No clients found"
                  }
                </p>
              </div>
            ) : (
              <>
                <ClientsTable
                  clients={paginatedClients}
                  onRowClick={handleClientClick}
                  onClientUpdate={handleClientUpdate}
                />

                {/* Pagination Controls */}
                {clientItemsPerPage !== 'all' && clientTotalPages > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 'var(--space-5)',
                      marginTop: 'var(--space-8)',
                      paddingTop: 'var(--space-5)',
                      borderTop: '1px solid var(--gray-600)',
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setClientCurrentPage(clientCurrentPage - 1)}
                      disabled={clientCurrentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>

                    <div style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-400)',
                      fontWeight: 'var(--font-medium)',
                    }}>
                      Page {clientCurrentPage} of {clientTotalPages}
                      <span style={{ marginLeft: 'var(--space-3)', color: 'var(--gray-500)', fontSize: 'var(--text-sm)' }}>
                        ({filteredClients.length} total clients)
                      </span>
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setClientCurrentPage(clientCurrentPage + 1)}
                      disabled={clientCurrentPage === clientTotalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                )}

                {/* Show total when viewing all */}
                {clientItemsPerPage === 'all' && (
                  <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-4)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-sm)',
                    marginTop: 'var(--space-4)',
                  }}>
                    Showing all {filteredClients.length} clients
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>

      {modalOpen && selectedProject && (
        <OperationsDashboardModal
          project={selectedProject}
          clients={clients}
          contacts={contacts}
          modelQuotes={allModelQuotes}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          onDelete={handleProjectDelete}
        />
      )}

      {quoteModalOpen && selectedQuote && (
        <ModelQuotesModal
          quote={selectedQuote}
          clients={clients}
          contacts={contacts}
          isOpen={quoteModalOpen}
          onClose={handleQuoteModalClose}
          onSave={handleQuoteModalSave}
        />
      )}

      {clientModalOpen && selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          isOpen={clientModalOpen}
          onClose={handleClientModalClose}
        />
      )}
    </div>
  );
};

export default OperationsDashboard;
